import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    patient_name: '',
    treatment_type: '',
    year: new Date().getFullYear().toString(),
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Sample testimonials to show while loading
  const sampleTestimonials = [
    {
      id: '1',
      patient_name: 'Sample Patient',
      treatment_type: 'Neurological Rehabilitation',
      year: '2024',
      message: 'Excellent service and professional care. Highly recommended!',
      approved: true
    },
    {
      id: '2',
      patient_name: 'Sample Patient 2',
      treatment_type: 'Post-Op Rehabilitation',
      year: '2024',
      message: "Bong's expertise and caring approach made my recovery journey so much easier.",
      approved: true
    },
    {
      id: '3',
      patient_name: 'Sample Patient 3',
      treatment_type: 'Dry Needling',
      year: '2024',
      message: 'Professional, knowledgeable and compassionate. Thank you for the excellent care!',
      approved: true
    }
  ];

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?approved_only=true`);
      setTestimonials(response.data.length > 0 ? response.data : sampleTestimonials);
    } catch (error) {
      setTestimonials(sampleTestimonials);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/testimonials`, formData);
      toast.success('Thank you! Your testimonial has been submitted for review.');
      setFormData({
        patient_name: '',
        treatment_type: '',
        year: new Date().getFullYear().toString(),
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit testimonial. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = [
    { label: 'Years Experience', value: '9+' },
    { label: 'Patients Treated', value: '500+' },
    { label: 'Satisfaction Rate', value: '95%' }
  ];

  return (
    <Layout>
      {/* Hero Section - Turquoise */}
      <section className="py-16 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 animate-fade-in" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Patient Testimonials
          </h1>
          <p className="text-lg text-slate-800 max-w-2xl mx-auto">
            Hear from our patients about their recovery journey
          </p>
        </div>
      </section>

      {/* Separator Line */}
      <div className="border-t-4 border-slate-400 w-full"></div>

      {/* Testimonials Grid - White */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id}
                className="bg-slate-50 rounded-2xl p-8 relative animate-fade-in card-hover"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`testimonial-card-${index}`}
              >
                <Quote className="w-10 h-10 text-[#5ce1e6] opacity-30 absolute top-6 right-6" />
                
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#5ce1e6] text-[#5ce1e6]" />
                  ))}
                </div>
                
                <p className="text-slate-600 mb-6 italic">
                  "{testimonial.message}"
                </p>
                
                <div className="border-t border-slate-200 pt-4">
                  <p className="font-semibold text-slate-800">{testimonial.patient_name}</p>
                  <p className="text-sm text-slate-500">
                    {testimonial.treatment_type} • {testimonial.year}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                <div className="stat-number text-slate-900">{stat.value}</div>
                <p className="text-slate-700 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Testimonial Form - White */}
      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Share Your Experience
            </h2>
            <p className="text-slate-600">
              We'd love to hear about your experience with BN Physiotherapy. Your feedback helps us improve our services.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Your Name</label>
                <Input
                  value={formData.patient_name}
                  onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                  placeholder="Enter your name"
                  required
                  data-testid="testimonial-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Treatment Type</label>
                <Input
                  value={formData.treatment_type}
                  onChange={(e) => setFormData({ ...formData, treatment_type: e.target.value })}
                  placeholder="e.g., Dry Needling, Post-Op Rehabilitation"
                  required
                  data-testid="testimonial-treatment-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                <Input
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="2024"
                  required
                  data-testid="testimonial-year-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Your Message</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Share your experience..."
                  rows={4}
                  required
                  data-testid="testimonial-message-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#5ce1e6] text-slate-900 hover:bg-[#22d3ee]"
                disabled={submitting}
                data-testid="testimonial-submit-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Testimonial'}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}
